import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { useSelector, useDispatch } from 'react-redux';
import { fetchPaymentDashboardData } from '../../../../redux/reducers/paymentDashboardSlice';
import './RevenueReport.css';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

const RevenueReport = () => {
  const dispatch = useDispatch();
  const { revenueReport, loading } = useSelector((state) => state.paymentDashboard);
  const [view, setView] = useState('YEAR');
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    dispatch(fetchPaymentDashboardData());
  }, [dispatch]);

  useEffect(() => {
    if (!revenueReport || !revenueReport[view]) return;

    const data = revenueReport[view] || [];

    const chartSeries = ['Clinic', 'Pharmacy', 'Pet Shop'].map((type) => {
      const match = data.find((item) => item.type === type);
      return {
        name: type,
        data: [match?.total || 0],
      };
    });

    // Set categories based on view
    let cats = [];
    if (view === 'WEEK') cats = ['This Week'];
    else if (view === 'MONTH') cats = ['This Month'];
    else if (view === 'YEAR') cats = ['This Year'];

    setSeries(chartSeries);
    setCategories(cats);
  }, [revenueReport, view]);

  const options = {
    chart: {
      type: 'line',
      toolbar: { show: false },
    },
    stroke: { 
      width: 3, 
      curve: 'smooth',
      dashArray: [5, 5, 0] // Dashed lines for first two series, solid for third
    },
    markers: {
      size: 6,
      strokeWidth: 2,
      strokeColors: ['#94A3B8', '#F472B6', '#3B82F6'],
      fillColors: ['#94A3B8', '#F472B6', '#3B82F6'],
    },
    xaxis: {
      categories: categories,
    },
    yaxis: {
      labels: {
        formatter: (val) =>
          val >= 1000000
            ? `${(val / 1000000).toFixed(1)}M`
            : val >= 1000
            ? `${(val / 1000).toFixed(1)}k`
            : val,
      },
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'center',
      offsetY: 0,
      markers: { 
        width: 12, 
        height: 12, 
        radius: 6,
        fillColors: ['#94A3B8', '#F472B6', '#3B82F6']
      },
    },
    title: {
      text: 'Revenue Report',
      align: 'left',
      style: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#333',
        fontFamily: 'inherit'
      },
      offsetY: 0,
      margin: 0
    },
    colors: ['#94A3B8', '#F472B6', '#3B82F6'],
    grid: {
      show: true,
      strokeDashArray: 3,
      borderColor: '#e0e0e0',
    },
  };

  if (loading && !series.length) {
    return (
      <div className="revenue-container">
        <div className="chart-card">Loading chart...</div>
      </div>
    );
  }

  return (
    <div className="revenue-container">
      <div className="header" style={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <Box display="flex" justifyContent="flex-end">
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(_, val) => val && setView(val)}
            size="small"
            sx={{
              backgroundColor: '#f0f2f5',
              borderRadius: '12px',
              '& .MuiToggleButton-root': {
                border: 'none',
                padding: '6px 16px',
                textTransform: 'none',
                fontWeight: 500,
                color: '#333',
              },
              '& .Mui-selected': {
                borderRadius: '7px',
                backgroundColor: '#005EB8',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#004a99',
                },
              },
            }}
          >
            <ToggleButton value="WEEK">Week</ToggleButton>
            <ToggleButton value="MONTH">Month</ToggleButton>
            <ToggleButton value="YEAR">Year</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </div>
      
      <div className="chart-card">
        <Chart options={options} series={series} type="line" height={300} />
      </div>
    </div>
  );
};

export default RevenueReport;